import React, { useMemo } from 'react'
import ReportElementSettings from './ReportElementSettings'
import BoxAndWhiskerChartElement from 'components/BoxAndWhiskerChartElement'
import {
  AxisLabelsFormatterCallbackFunction,
  SeriesOptionsType,
} from 'highcharts'
import { Box, Typography, Paper } from '@mui/material'
import useReportElementChart from 'hooks/useReportElementChart'
import { getBarChartColors, getSubgroupToUse } from 'util/getBarChartData'
import useElementCohortsAndSubgroups from 'hooks/useElementCohortsAndSubgroups'
import { ReportElementProps } from './ReportElement'

const ReportElementBoxAndWhiskerChart: React.FC<ReportElementProps> = ({
  reportId,
  element,
  sectionId,
  report,
  favorites = false,
  print = false,
  onUpdateFavorites,
}) => {
  const {
    cohort,
    subgroup,
    pastResults,
    dataToggles,
    data,
    parsedContent,
    parsedData,
    elementTitle,
    pastResultNames,
    hidden,
    handleSetHidden,
  } = useReportElementChart(element, reportId)

  try {
    const [elementCohorts, elementSubgroups] = useElementCohortsAndSubgroups(
      element,
      parsedData
    )

    // const cohortToUse: string = getCohortToUse(cohort || '', elementCohorts)
    const subgroupToUse: string = getSubgroupToUse(
      subgroup || '',
      elementSubgroups
    )

    const chartOptions = useMemo(() => {
      let seriesData: SeriesOptionsType[] = []
      if (data && dataToggles === 'Overall') {
        seriesData = seriesData.concat(data.current || [])
        if (pastResults) {
          seriesData = seriesData.concat(data['past_results'] || [])
        }
      }
      if (data && dataToggles === 'Subgroup') {
        const subgroupData = subgroup
          ? data.segmentations.filter(
              (d: { name: string }) => d.name === subgroupToUse
            )[0]
          : data.segmentations[0]
        if (subgroupData) {
          seriesData = subgroupData.series
        }
      }

      seriesData = seriesData.filter((d) => !!d)

      const showHideNames: string[] = seriesData.map((d) => d.name || '')

      const chartColors = getBarChartColors(seriesData.length)
      seriesData = seriesData.map((d: any, i: number) => ({
        ...d,
        fillColor: chartColors[i],
        color: chartColors[i],
        whiskerColor: '#000000',
        medianColor: '#000000',
        stemColor: '#000000',
        data: [d.data],
      }))

      const options = {
        title: undefined,
        colors: [],
        exporting: {
          buttons: {
            contextButton: {
              enabled: false,
            },
          },
        },
        chart: {
          type: 'boxplot',
        },
        xAxis: {
          categories: [''],
          title: {
            text: parsedContent.x_axis_label,
          },
        },
        yAxis: {
          min: data?.yMin,
          max: data?.yMax,
          title: {
            text: parsedContent.y_axis_label,
          },
          labels: {
            formatter: function () {
              const label = this.axis.defaultLabelFormatter.call(this)
              return `${label}%`
            } as AxisLabelsFormatterCallbackFunction,
          },
        },
        plotOptions: {
          boxplot: {
            tooltip: {
              valueSuffix: '%',
            },
            maxPointWidth: 75,
            groupPadding: 0.05,
          },
        },
        credits: {
          enabled: false,
        },
        series: seriesData.filter((d) => !hidden.includes(d.name)),
      }
      return { options, showHideNames }
    }, [data, parsedData, parsedContent, cohort, subgroup, pastResults, hidden])

    const showHideOptions = chartOptions.showHideNames
      .filter((item: string) =>
        pastResults ? true : !pastResultNames.includes(item)
      )
      .map((item: string) => ({
        key: item,
        title: item,
      }))

    return (
      <Box sx={{ mb: 4, width: '100%' }}>
        {elementTitle && (
          <Box px={4}>
            <Typography variant="h5">{elementTitle}</Typography>
          </Box>
        )}
        <Box mt={2}>
          <ReportElementSettings
            print={print}
            reportId={reportId}
            element={element}
            sectionId={sectionId}
            report={report}
            noSubgroup={
              dataToggles === 'Overall' || data.segmentations.length === 0
            }
            noCohort={true}
            noPastResults={
              dataToggles != 'Overall' ||
              (data['past_results'] || []).length === 0
            }
            showHideOptions={showHideOptions}
            hidden={hidden}
            onChangeHidden={handleSetHidden}
            favorites={favorites}
            onUpdateFavorites={onUpdateFavorites}
          />
        </Box>
        <Box p={4}>
          <BoxAndWhiskerChartElement options={chartOptions.options} />
        </Box>
      </Box>
    )
  } catch (error) {
    console.error(error)
    return null
  }
}

export default ReportElementBoxAndWhiskerChart
